<template>
  <ItemPage
    title="Configuração do Portal - Status"
    size="md"
    :actions="actions"
  >
    <template v-if="isGodAdmin">
      <v-container v-if="configError">
        <v-alert
          type="error"
          prominent
        >
          <div class="text-h6 mb-2">
            Erro de Configuração
          </div>
          {{ configError }}
        </v-alert>
      </v-container>
      <v-form
        v-else
        ref="form"
      >
        <v-container v-if="loading && !statusDataLoaded">
          <v-row>
            <v-col
              cols="12"
              class="text-center"
            >
              <v-progress-circular
                indeterminate
                color="primary"
              />
              <p class="mt-4">
                Carregando status atual...
              </p>
            </v-col>
          </v-row>
        </v-container>
        <v-container v-else>
          <v-switch
            v-model="statusData.maintenance"
            :disabled="loading"
            label="Manutenção"
            hint="Quando ativado, o portal entrará em modo de manutenção"
            persistent-hint
            @change="onMaintenanceChange"
          />

          <v-textarea
            v-if="statusData.maintenance"
            v-model="statusData.message"
            :rules="rulesMessage"
            :disabled="loading"
            :loading="loading"
            label="Mensagem de Manutenção"
            hint="Mensagem que será exibida quando o portal estiver em manutenção"
            persistent-hint
            rows="3"
            class="mt-4"
            @input="checkUpdate"
          />

          <div class="text-h6 mt-6 mb-2">
            Versões
          </div>
          <v-row>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="statusData.version.web"
                :rules="rulesVersion"
                :disabled="loading"
                label="Versão Web"
                @input="checkUpdate"
              />
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="statusData.version.android"
                :rules="rulesVersion"
                :disabled="loading"
                label="Versão Android"
                @input="checkUpdate"
              />
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="statusData.version.ios"
                :rules="rulesVersion"
                :disabled="loading"
                label="Versão iOS"
                @input="checkUpdate"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </template>
    <template v-else>
      <v-container>
        <v-alert
          type="warning"
          prominent
        >
          Você não tem permissão para acessar esta página.
        </v-alert>
      </v-container>
    </template>
  </ItemPage>
</template>

<script>
import ItemPage from '@/components/pages/ItemPage.vue'
import utils from '@/api/utils.js'

export default {
  components: {
    ItemPage
  },
  data () {
    return {
      loading: false,
      configError: null,
      statusDataLoaded: false,
      btnDisable: true,
      statusData: {
        maintenance: false,
        message: null,
        version: {
          ios: '',
          android: '',
          web: ''
        }
      },
      currentStatusData: {},
      rulesMessage: [
        value => !!value || 'Preenchimento obrigatório.'
      ],
      rulesVersion: [
        value => !!value || 'Preenchimento obrigatório.'
      ]
    }
  },
  computed: {
    isGodAdmin () {
      return utils.checkIsGodAdmin()
    },
    actions () {
      return [
        {
          label: 'Salvar',
          action: this.updateStatus,
          icon: 'mdi-floppy',
          disabled: this.btnDisable || this.loading
        },
        {
          label: 'Abrir Arquivo',
          action: this.openStatusFile,
          icon: 'mdi-open-in-new',
          disabled: this.loading
        }
      ]
    }
  },
  async created () {
    if (this.isGodAdmin) {
      await this.loadCurrentStatus()
    }
    // Initialize currentStatusData after loading (or with defaults) - deep clone to avoid reference sharing
    this.currentStatusData = JSON.parse(JSON.stringify(this.statusData))
  },
  methods: {
    async loadCurrentStatus () {
      this.loading = true
      try {
        const currentStatus = await this.$api.getPortalStatus()
        if (!currentStatus) {
          return
        }

        this.statusData = {
          maintenance: currentStatus.maintenance || false,
          message: currentStatus.message || null,
          version: {
            ios: currentStatus.version?.ios || '',
            android: currentStatus.version?.android || '',
            web: currentStatus.version?.web || ''
          }
        }
        // Update currentStatusData after loading (deep clone to avoid reference sharing)
        this.currentStatusData = JSON.parse(JSON.stringify(this.statusData))
        this.statusDataLoaded = true
      } catch (e) {
        // Check if it's a missing env var error
        if (e.message && e.message.includes('VITE_PORTAL_CONFIG_STATUS_URL')) {
          this.configError = 'A variável de ambiente VITE_PORTAL_CONFIG_STATUS_URL não está definida. Por favor, configure-a no arquivo .env'
          return
        }

        // If status file doesn't exist or can't be loaded, start with empty form
        this.statusDataLoaded = true
        this.$store.dispatch('setSnackBar', {
          text: 'Não foi possível carregar o status atual. Você pode criar uma nova configuração.',
          color: 'info'
        })
      } finally {
        this.loading = false
      }
    },
    onMaintenanceChange () {
      if (!this.statusData.maintenance) {
        // Clear message when maintenance is disabled
        this.statusData.message = null
        this.checkUpdate()
        return
      }

      // Set default message when maintenance is enabled
      if (!this.statusData.message) {
        this.statusData.message = 'Estamos em manutenção. tente novamente mais tarde'
      }
      this.checkUpdate()
    },
    checkUpdate () {
      const data = utils.diffObjects(this.statusData, this.currentStatusData)
      this.btnDisable = Object.keys(data).length === 0
      return data
    },
    async updateStatus () {
      const validate = await this.validate()
      if (!validate) {
        return false
      }

      this.loading = true
      try {
        await this.$api.updatePortalStatus(this.statusData)
        this.$store.dispatch('setSnackBar', { text: 'Configuração atualizada com sucesso' })
        // Deep clone to avoid reference sharing
        this.currentStatusData = JSON.parse(JSON.stringify(this.statusData))
        this.btnDisable = true
      } catch (e) {
        this.$store.dispatch('setSnackBar', { text: 'Não foi possível atualizar, tente novamente mais tarde.', color: 'red' })
      } finally {
        this.loading = false
      }
    },
    validate () {
      return this.$refs.form.validate()
    },
    openStatusFile () {
      const statusUrl = import.meta.env.VITE_PORTAL_CONFIG_STATUS_URL
      if (!statusUrl) {
        this.$store.dispatch('setSnackBar', {
          text: 'URL do arquivo de status não está configurada',
          color: 'warning'
        })
        return
      }

      window.open(statusUrl, '_blank')
    }
  }
}
</script>
